import React, { useRef, useCallback, useState, useEffect } from 'react';
import { GoogleMap, useLoadScript, Circle, Polygon, Marker, OverlayView } from '@react-google-maps/api';
import { Box, TextField, CircularProgress, Typography } from '@mui/material';
import config from '../../config.js';
import { useTranslation } from 'react-i18next';
import SearchIcon from '@mui/icons-material/Search';
import InputAdornment from '@mui/material/InputAdornment';

const libraries = ['places'];

// Custom marker icon
const createCustomIcon = (label) => ({
  url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="40" height="40">
      <circle cx="50" cy="25" r="20" fill="#183059"/>
      <rect x="45" y="40" width="10" height="50" fill="#183059"/>
      ${label ? `<circle cx="50" cy="25" r="12" fill="white"/>
                  <text x="50" y="30" text-anchor="middle" font-size="12" font-weight="bold" fill="#183059">${label}</text>` : ''}
    </svg>
  `)}`,
  scaledSize: new window.google.maps.Size(40, 40),
  anchor: new window.google.maps.Point(20, 40)
});

// Custom Coordinate Header Component
const CoordinateHeader = ({ position, coordinates, label }) => {
  return (
    <div
      style={{
        position: 'absolute',
        left: '50%',
        top: '-100px',
        transform: 'translateX(-50%)',
        background: '#2D3E50',
        color: 'white',
        padding: '8px 12px',
        borderRadius: '8px',
        fontSize: '13px',
        fontFamily: 'Arial, sans-serif',
        fontWeight: '400',
        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
        whiteSpace: 'nowrap',
        textAlign: 'left',
        minWidth: '180px',
        zIndex: 1000,
        pointerEvents: 'none'
      }}
    >
      <div style={{ marginBottom: '2px', lineHeight: '1.3' }}>
        Longitude: {coordinates.lng ? coordinates.lng.toFixed(6) : ''}
      </div>
      <div style={{ lineHeight: '1.3' }}>
        Latitude: {coordinates.lat ? coordinates.lat.toFixed(6) : ''}
      </div>
      <div style={{
        position: 'absolute',
        bottom: '-6px',
        left: '50%',
        transform: 'translateX(-50%) rotate(45deg)',
        width: '12px',
        height: '12px',
        background: '#2D3E50',
        borderRadius: '0 0 2px 0'
      }} />
    </div>
  );
};

export default function MapSection({
  boundaryType,
  site,
  mapCenter,
  mapZoom,
  mapLoaded,
  onMapLoad,
  onMapClick,
  onMarkerDrag,
  onPlaceSelected,
  isMobile,
  siteGates = [],
  pendingAttachedGates = [],
  pendingGateCoordinates = null,
  pendingNewGates = [],
}) {
  const { t } = useTranslation();
  const mapContainerStyle = {
    width: '100%',
    borderRadius: "3px",
    border: "1px solid #183059",
    height: isMobile ? '300px' : '500px',
  };

  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: config.googleMapsApiKey || '',
    libraries,
  });

  const handlePlaceChanged = useCallback(() => {
    if (autocompleteRef.current) {
      const place = autocompleteRef.current.getPlace();
      if (place && place.geometry) {
        onPlaceSelected(place);
      }
    }
  }, [onPlaceSelected]);

  const initializeAutocomplete = useCallback(() => {
    if (window.google && window.google.maps && inputRef.current && !autocompleteRef.current) {
      autocompleteRef.current = new window.google.maps.places.Autocomplete(inputRef.current, {
        fields: ['geometry', 'name'],
      });
      autocompleteRef.current.addListener('place_changed', handlePlaceChanged);
    }
  }, [handlePlaceChanged]);

  useEffect(() => {
    if (isLoaded && inputRef.current) {
      initializeAutocomplete();
    }
  }, [isLoaded, initializeAutocomplete]);

  const handleMarkerDragEnd = useCallback((index, event) => {
    const newLat = event.latLng.lat();
    const newLng = event.latLng.lng();
    onMarkerDrag(index, {
      latLng: {
        lat: () => newLat,
        lng: () => newLng
      }
    });
  }, [onMarkerDrag]);

  if (loadError) {
    return (
      <Box sx={{ textAlign: 'center', padding: '20px' }}>
        <Typography color="error">Map cannot be loaded right now, sorry.</Typography>
      </Box>
    );
  }

  if (!isLoaded) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: mapContainerStyle.height }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        bgcolor: 'white',
        boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ zIndex: 1, borderRadius: '16px' }}>
        <TextField
          fullWidth
          placeholder={t("Search")}
          variant="outlined"
          size="small"
          sx={{
            zIndex: 1,
            width: isMobile ? '50%' : '30%',
            margin: '16px',
            position: 'absolute',
            '& .MuiOutlinedInput-root': {
              color: '#1F242E',
              borderRadius: '4px',
              border: "1px solid #414958",
              backgroundColor: '#FFFFFF',
              "& fieldset": {
                border: "none",
              },
            },
          }}
          inputRef={inputRef}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <SearchIcon sx={{ color: "#183059" }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      <Box sx={{ width: '100%', overflow: 'hidden', borderRadius: "3px" }}>
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={mapCenter}
          zoom={mapZoom}
          onLoad={onMapLoad}
          onClick={onMapClick}
        >
          {/* Circular boundary marker */}
          {mapLoaded && boundaryType === 'circular' && site.sitCoordinates[0]?.latitude && site.sitCoordinates[0]?.longitude && (
            <>
              <Marker
                position={{
                  lat: parseFloat(site.sitCoordinates[0].latitude),
                  lng: parseFloat(site.sitCoordinates[0].longitude),
                }}
                draggable={true}
                onDragEnd={(event) => handleMarkerDragEnd(0, event)}
                icon={createCustomIcon()}
              />
              <OverlayView
                position={{
                  lat: parseFloat(site.sitCoordinates[0].latitude),
                  lng: parseFloat(site.sitCoordinates[0].longitude),
                }}
                mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
              >
                <CoordinateHeader
                  coordinates={{
                    lat: parseFloat(site.sitCoordinates[0].latitude),
                    lng: parseFloat(site.sitCoordinates[0].longitude),
                  }}
                />
              </OverlayView>
            </>
          )}

          {/* Custom boundary markers */}
          {mapLoaded &&
            boundaryType === 'custom' &&
            site.sitCoordinates.map(
              (coord, index) =>
                (coord.latitude || coord.longitude) && (
                  <React.Fragment key={index}>
                    <Marker
                      position={{
                        lat: parseFloat(coord.latitude) || 0,
                        lng: parseFloat(coord.longitude) || 0,
                      }}
                      draggable={true}
                      onDragEnd={(event) => handleMarkerDragEnd(index, event)}
                      icon={createCustomIcon((index + 1).toString())}
                    />
                    <OverlayView
                      position={{
                        lat: parseFloat(coord.latitude) || 0,
                        lng: parseFloat(coord.longitude) || 0,
                      }}
                      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                    >
                      <CoordinateHeader
                        coordinates={{
                          lat: parseFloat(coord.latitude) || 0,
                          lng: parseFloat(coord.longitude) || 0,
                        }}
                        label={`Point ${index + 1}`}
                      />
                    </OverlayView>
                  </React.Fragment>
                )
            )}

          {/* Circular boundary */}
          {mapLoaded && boundaryType === 'circular' && site.radius && site.sitCoordinates[0]?.latitude && site.sitCoordinates[0]?.longitude && (
            <Circle
              center={{
                lat: parseFloat(site.sitCoordinates[0].latitude),
                lng: parseFloat(site.sitCoordinates[0].longitude),
              }}
              radius={parseFloat(site.radius) * 1000}
              options={{
                fillColor: '#95A0B3',
                fillOpacity: 0.3,
                strokeColor: '#183059',
                strokeOpacity: 0.8,
                strokeWeight: 2,
              }}
            />
          )}

          {/* Custom polygon boundary */}
          {mapLoaded && boundaryType === 'custom' && site.sitCoordinates.length >= 3 && (
            <Polygon
              paths={site.sitCoordinates
                .filter(coord => coord.latitude && coord.longitude)
                .map((coord) => ({
                  lat: parseFloat(coord.latitude) || 0,
                  lng: parseFloat(coord.longitude) || 0,
                }))}
              options={{
                fillColor: '#95A0B3',
                fillOpacity: 0.3,
                strokeColor: '#183059',
                strokeOpacity: 0.8,
                strokeWeight: 2,
                strokePattern: [2, 2]
              }}
            />
          )}

          {/* Existing gates linked to this site */}
          {mapLoaded &&
            siteGates.map((gate) => {
              const lat = Number(gate.latitude);
              const lng = Number(gate.longitude);
              if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
              return (
                <Marker
                  key={`site-gate-${gate.id}`}
                  position={{ lat, lng }}
                  icon={createCustomIcon("G")}
                  title={gate.name || gate.stopId || "Gate"}
                />
              );
            })}

          {/* Pending gates attached via UI (not saved yet) */}
          {mapLoaded &&
            pendingAttachedGates.map((gate) => {
              const lat = Number(gate.latitude);
              const lng = Number(gate.longitude);
              if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
              return (
                <Marker
                  key={`pending-attached-gate-${gate.id}`}
                  position={{ lat, lng }}
                  icon={createCustomIcon("G")}
                  title={gate.name || gate.stopId || "Gate (pending attach)"}
                />
              );
            })}

          {/* New gate point selected while creating/updating site */}
          {mapLoaded && pendingGateCoordinates && (
            <Marker
              position={pendingGateCoordinates}
              icon={createCustomIcon("N")}
              title="New gate point"
            />
          )}

          {mapLoaded &&
            pendingNewGates.map((gate) => {
              if (Number.isNaN(gate.latitude) || Number.isNaN(gate.longitude)) return null;
              return (
                <Marker
                  key={`pending-gate-${gate.id}`}
                  position={{ lat: gate.latitude, lng: gate.longitude }}
                  icon={createCustomIcon("N")}
                  title={gate.name || gate.stopId || "Pending gate"}
                />
              );
            })}

        </GoogleMap>
      </Box>
    </Box>
  );
}